'use client';

import { useState, useEffect } from 'react';
import HeroCarousel from './HeroCarousel';
import NewsSection from './NewsSection';
import MarketplaceSection from './MarketplaceSection';
import JobsSection from './JobsSection';
import AdZone from './AdZone';
import Link from './Link';
import Icon from './Icon';
import { articleService } from '@/services/articleService';
import { listingService } from '@/services/listingService';
import { createClient } from '@/lib/supabase/client';

const supabase = createClient();

interface CarouselItem {
  id: number;
  type: 'article' | 'listing' | 'job';
  title: string;
  description: string;
  image: string;
  alt: string;
  link: string;
  badge?: string;
}

interface Article {
  id: string;
  slug?: string;
  title: string;
  excerpt: string;
  image: string;
  alt: string;
  author: string;
  publishedAt: string;
  category: string;
  readTime: number;
}

interface Equipment {
  id: string;
  title: string;
  price: string;
  location: string;
  image: string;
  alt: string;
  seller: string;
  year: number;
  hours: string;
  condition: string;
  whatsappNumber: string;
}

interface Job {
  id: string;
  title: string;
  company: string;
  location: string;
  type: string;
  salary: string;
  logo: string;
  alt: string;
  deadline: string;
  postedAt: string;
  featured: boolean;
}

const HomeInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);

  useEffect(() => {
    setIsHydrated(true);
    loadHomePageData();
  }, []);

  const loadHomePageData = async () => {
    try {
      setLoading(true);
      setError('');

      // Load articles, equipment, and jobs in parallel
      const [articlesData, equipmentData, jobsData] = await Promise.all([
        articleService.getFeatured(3),
        listingService.getFeaturedListings(3),
        loadFeaturedJobs()
      ]);

      // Transform articles data
      const transformedArticles: Article[] = (articlesData || [])?.map((article: any) => ({
        id: article?.id || '',
        slug: article?.slug || '',
        title: article?.title || '',
        excerpt: article?.excerpt || '',
        image: article?.featured_image_url || '',
        alt: article?.featured_image_alt || article?.title || 'Article image',
        author: article?.user_profiles?.full_name || 'Anonymous',
        publishedAt: article?.published_date ? new Date(article.published_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '',
        category: article?.article_categories?.name || 'General',
        readTime: article?.read_time_minutes || 5
      }));

      // Transform equipment data
      const transformedEquipment: Equipment[] = (equipmentData || [])?.map((item: any) => ({
        id: item?.id || '',
        title: item?.title || '',
        price: item?.price ? `$${Number(item.price).toLocaleString()}` : 'Contact for price',
        location: item?.location || '',
        image: item?.images?.[0]?.image_url || '',
        alt: item?.images?.[0]?.alt_text || item?.title || 'Equipment image',
        seller: item?.seller?.full_name || 'Anonymous Seller',
        year: item?.year || new Date().getFullYear(),
        hours: item?.mileage_hours || 'Not specified',
        condition: item?.condition || 'used',
        whatsappNumber: item?.seller?.phone || ''
      }));

      // Transform jobs data
      const transformedJobs: Job[] = (jobsData || [])?.map((job: any) => ({
        id: job?.id || '',
        title: job?.title || '',
        company: job?.company?.name || 'Company',
        location: job?.location || '',
        type: job?.job_type || 'full-time',
        salary: job?.salary_min && job?.salary_max
          ? `$${Number(job.salary_min).toLocaleString()} - $${Number(job.salary_max).toLocaleString()}`
          : 'Competitive',
        logo: job?.company?.logo_url || '',
        alt: `${job?.company?.name || 'Company'} logo`,
        deadline: job?.expires_at ? new Date(job.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '',
        postedAt: job?.created_at ? formatPostedDate(job.created_at) : '',
        featured: job?.featured || false
      }));

      setArticles(transformedArticles);
      setEquipment(transformedEquipment);
      setJobs(transformedJobs);

      // Build carousel from featured content
      const carousel: CarouselItem[] = [];

      if (transformedArticles?.[0]) {
        carousel.push({
          id: 1,
          type: 'article',
          title: transformedArticles[0].title,
          description: transformedArticles[0].excerpt,
          image: transformedArticles[0].image,
          alt: transformedArticles[0].alt,
          link: `/article-detail?slug=${transformedArticles[0].slug}`,
          badge: 'Breaking News'
        });
      }

      if (transformedEquipment?.[0]) {
        carousel.push({
          id: 2,
          type: 'listing',
          title: transformedEquipment[0].title,
          description: `${transformedEquipment[0].year} model in ${transformedEquipment[0].condition} condition. ${transformedEquipment[0].hours} hours.`,
          image: transformedEquipment[0].image,
          alt: transformedEquipment[0].alt,
          link: `/equipment-detail?id=${transformedEquipment[0].id}`,
          badge: 'Featured Listing'
        });
      }

      if (transformedJobs?.[0]) {
        carousel.push({
          id: 3,
          type: 'job',
          title: transformedJobs[0].title,
          description: `${transformedJobs[0].company} is hiring in ${transformedJobs[0].location}. ${transformedJobs[0].salary} salary.`,
          image: transformedJobs[0].logo || 'https://images.unsplash.com/photo-1675815584575-4a303723b906',
          alt: transformedJobs[0].alt,
          link: `/job-detail?id=${transformedJobs[0].id}`,
          badge: 'Hot Job'
        });
      }

      setCarouselItems(carousel);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load homepage data');
    } finally {
      setLoading(false);
    }
  };

  const loadFeaturedJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select(`
          id,
          title,
          location,
          job_type,
          salary_min,
          salary_max,
          salary_currency,
          featured,
          expires_at,
          created_at,
          company:companies(
            name,
            logo_url
          )
        `)
        .eq('status', 'approved')
        .eq('featured', true)
        .order('created_at', { ascending: false })
        .limit(3);

      if (error) throw error;
      return data || [];
    } catch (err) {
      return [];
    }
  };

  const formatPostedDate = (dateString: string): string => {
    const posted = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - posted.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 14) return '1 week ago';
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return posted.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (!isHydrated || loading) {
    return (
      <div className="space-y-12">
        <div className="h-96 lg:h-[500px] bg-muted animate-pulse" />
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-3 space-y-12">
              <div className="space-y-6">
                <div className="h-8 bg-muted rounded animate-pulse" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-muted rounded-lg h-80 animate-pulse" />
                  ))}
                </div>
              </div>
            </div>
            <div className="space-y-6">
              <div className="h-64 bg-muted rounded-lg animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
          <p className="text-destructive font-medium mb-4">{error}</p>
          <button 
            onClick={loadHomePageData}
            className="bg-primary text-primary-foreground px-6 py-2 rounded-industrial font-heading font-bold uppercase tracking-wide text-sm hover:bg-accent transition-industrial"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* Full-width Hero Carousel */}
      <div className="w-full">
        {carouselItems.length > 0 ? (
          <HeroCarousel items={carouselItems} />
        ) : (
          <div className="relative h-96 lg:h-[500px] bg-gradient-to-r from-primary/20 to-secondary/20 flex items-center justify-center">
            <div className="text-center px-6">
              <h1 className="text-4xl lg:text-6xl font-heading font-bold text-foreground mb-4">
                Ireland's Heavy Machinery Platform
              </h1>
              <p className="text-lg lg:text-xl text-text-secondary max-w-2xl mx-auto">
                Connect with equipment buyers, find skilled operators, and stay informed with industry news.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Container for remaining content */}
      <div className="max-w-7xl mx-auto px-6">
        {/* Homepage Leaderboard Ad */}
        <div className="flex justify-center mb-12">
          <AdZone zone="homepage_leaderboard" />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-12">
            {/* News Section */}
            <NewsSection articles={articles} />

            {/* Global Leaderboard Ad */}
            <div className="flex justify-center">
              <AdZone zone="global_leaderboard" />
            </div>

            {/* Marketplace Section */}
            <MarketplaceSection equipment={equipment} />

            {/* Jobs Section */}
            <JobsSection jobs={jobs} />
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Sidebar MPU Ad */}
            <AdZone zone="sidebar_mpu" />

            {/* Post Jobs Section - Moved from main body */}
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 text-center">
              <div className="w-12 h-12 bg-primary rounded-industrial flex items-center justify-center mx-auto mb-3">
                <Icon name="BriefcaseIcon" size={24} className="text-primary-foreground" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground uppercase tracking-wide mb-2">
                Hiring? Post Jobs Here
              </h3>
              <p className="text-text-secondary mb-4 text-sm leading-relaxed">
                Reach thousands of qualified heavy machinery operators and professionals.
              </p>
              <ul className="text-left text-xs text-text-secondary mb-4 space-y-1">
                <li className="flex items-center">
                  <Icon name="CheckIcon" size={14} className="text-green-500 mr-2 flex-shrink-0" />
                  45,000+ daily active job seekers
                </li>
                <li className="flex items-center">
                  <Icon name="CheckIcon" size={14} className="text-green-500 mr-2 flex-shrink-0" />
                  Advanced skill filtering
                </li>
                <li className="flex items-center">
                  <Icon name="CheckIcon" size={14} className="text-green-500 mr-2 flex-shrink-0" />
                  Direct messaging included
                </li>
              </ul>
              <Link
                href="/post-job"
                className="block bg-primary text-primary-foreground px-4 py-2 rounded-industrial font-heading font-bold uppercase tracking-wide text-xs hover:bg-accent transition-industrial mb-2"
              >
                Post a Job - Free Trial
              </Link>
              <p className="text-xs text-text-secondary">
                No setup fees • Cancel anytime
              </p>
            </div>

            {/* List Equipment Section - Moved from main body */}
            <div className="bg-secondary/5 border border-secondary/20 rounded-lg p-6 text-center">
              <div className="w-12 h-12 bg-secondary rounded-industrial flex items-center justify-center mx-auto mb-3">
                <Icon name="TruckIcon" size={24} className="text-secondary-foreground" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground uppercase tracking-wide mb-2">
                Selling Equipment? List It Here
              </h3>
              <p className="text-text-secondary mb-4 text-sm leading-relaxed">
                Connect with serious buyers across the construction industry nationwide.
              </p>
              <ul className="text-left text-xs text-text-secondary mb-4 space-y-1">
                <li className="flex items-center">
                  <Icon name="CheckIcon" size={14} className="text-green-500 mr-2 flex-shrink-0" />
                  15,000+ equipment searches daily
                </li>
                <li className="flex items-center">
                  <Icon name="CheckIcon" size={14} className="text-green-500 mr-2 flex-shrink-0" />
                  Professional photos included
                </li>
                <li className="flex items-center">
                  <Icon name="CheckIcon" size={14} className="text-green-500 mr-2 flex-shrink-0" />
                  Verified buyer network
                </li>
              </ul>
              <Link
                href="/list-equipment"
                className="block bg-secondary text-secondary-foreground px-4 py-2 rounded-industrial font-heading font-bold uppercase tracking-wide text-xs hover:bg-accent transition-industrial mb-2"
              >
                List Equipment - Free Trial
              </Link>
              <p className="text-xs text-text-secondary">
                Premium photos • Commission-free
              </p>
            </div>

            {/* Quick Registration CTA */}
            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 border border-border rounded-lg p-6">
              <h3 className="text-lg font-heading font-bold text-card-foreground mb-3 uppercase tracking-wide">
                Join Our Community
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                Get started in seconds with our streamlined registration. Post jobs, list equipment, or browse opportunities.
              </p>
              <Link
                href="/enhanced-authentication"
                className="block text-center bg-primary text-primary-foreground px-4 py-2 rounded-industrial font-heading font-bold uppercase tracking-wide text-sm hover:bg-accent transition-industrial mb-3"
              >
                Get Started Free
              </Link>
              <div className="text-xs text-text-secondary text-center space-y-1">
                <p>✓ Magic link login - no passwords</p>
                <p>✓ Post immediately after signup</p>
                <p>✓ Industry-verified network</p>
              </div>
            </div>

            {/* Newsletter Signup */}
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-heading font-bold text-card-foreground mb-3 uppercase tracking-wide">
                Industry Newsletter
              </h3>
              <p className="text-sm text-text-secondary mb-4">
                Newsletter coming soon. Follow us on social media for the latest equipment, jobs, and industry news updates.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeInteractive;